import React, { useEffect, useState } from "react";
import { useL } from "@/lib/useL";
import { Download, X, Share2 } from "lucide-react";
import { isRunningInTWA } from "@/lib/twa";

const DISMISS_KEY = "sux1_pwa_install_dismissed_at";
// If the user dismisses the prompt we hide it for this many days before
// giving them another gentle nudge. Keep it short so the install flow
// stays visible to users who hesitate.
const DISMISS_COOLDOWN_DAYS = 14;

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)")?.matches) return true;
  // iOS Safari exposes navigator.standalone on home-screen launches.
  if (window.navigator.standalone === true) return true;
  return false;
}

function dismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return false;
    const diffMs = Date.now() - dismissedAt;
    return diffMs < DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * Thin install banner shown on the Dashboard inviting the user to add
 * SUX1 to their home screen as a native-like PWA. Hidden when:
 *   - the app already runs standalone (iOS "Add to Home Screen" or
 *     Chromium PWA install)
 *   - the app runs inside the Google Play TWA wrapper
 *   - the user dismissed within the last 14 days
 *
 * Chromium: wires up the native `beforeinstallprompt` event and calls
 * `.prompt()` on click. iOS Safari: shows a short illustrated hint
 * ("Partager → Sur l'écran d'accueil") since Safari refuses the API.
 */
export default function PwaInstallPrompt() {
  const L = useL();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [hidden, setHidden] = useState(() => {
    if (typeof window === "undefined") return true;
    if (isRunningInTWA()) return true;
    if (isStandalone()) return true;
    if (dismissedRecently()) return true;
    return false;
  });
  const [iosExpanded, setIosExpanded] = useState(false);

  useEffect(() => {
    if (hidden) return;
    const onBIP = (e) => {
      // Chromium only — stash the event so we can call prompt() later.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setHidden(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    // iOS Safari path: no beforeinstallprompt — show the manual hint.
    if (isIOS() && !isStandalone()) {
      setShowIosHint(true);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [hidden]);

  if (hidden) return null;

  // Nothing to show: neither Chromium prompt nor iOS Safari.
  if (!deferredPrompt && !showIosHint) return null;

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setHidden(true);
      }
    } catch (_) {
      /* user cancelled — keep the prompt visible */
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
    setHidden(true);
  };

  return (
    <section
      data-testid="pwa-install-prompt"
      className="border border-[#002FA7]/30 bg-[#002FA7]/5 px-4 py-3 mb-4 flex items-center gap-3"
    >
      <Download className="w-4 h-4 text-[#002FA7] shrink-0" />
      <div className="flex-1 min-w-0">
        {deferredPrompt ? (
          <div className="text-sm leading-snug">
            <strong>{L("Installer SUX1 sur votre téléphone.", "Install SUX1 on your phone.")}</strong>{" "}
            <span className="text-muted-foreground">
              {L("Accès en 1 clic depuis votre écran d'accueil, sans navigateur.", "1-tap access from your home screen, no browser.")}
            </span>
          </div>
        ) : (
          <>
            <div className="text-sm leading-snug">
              <strong>{L("Ajouter SUX1 à votre écran d'accueil.", "Add SUX1 to your home screen.")}</strong>{" "}
              <button
                type="button"
                onClick={() => setIosExpanded((v) => !v)}
                data-testid="pwa-install-prompt-toggle-ios"
                className="underline text-[#002FA7] hover:text-[#FF2A00] transition-colors"
              >
                {iosExpanded ? L("Masquer", "Hide") : L("Voir comment", "Show how")}
              </button>
            </div>
            {iosExpanded && (
              <div className="mt-2 text-xs text-muted-foreground leading-relaxed">
                <ol className="list-decimal pl-4 space-y-0.5">
                  <li>
                    {L("Touchez l'icône", "Tap the")}{" "}
                    <Share2 className="inline w-3.5 h-3.5 mx-0.5 text-[#002FA7]" />{" "}
                    {L("« Partager » en bas de Safari.", "« Share » icon at the bottom of Safari.")}
                  </li>
                  <li>
                    {L("Faites défiler et choisissez « Sur l'écran d'accueil ».", "Scroll and choose « Add to Home Screen ».")}
                  </li>
                  <li>{L("Validez : SUX1 apparaît comme une app.", "Confirm: SUX1 appears like an app.")}</li>
                </ol>
              </div>
            )}
          </>
        )}
      </div>
      {deferredPrompt && (
        <button
          type="button"
          onClick={handleInstall}
          data-testid="pwa-install-prompt-install"
          className="shrink-0 font-mono uppercase tracking-[0.14em] text-[10px] px-3 py-2 bg-[#002FA7] text-white hover:bg-[#FF2A00] transition-colors"
        >
          Installer
        </button>
      )}
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="pwa-install-prompt-dismiss"
        aria-label="Masquer"
        title="Masquer pendant 14 jours"
        className="shrink-0 text-muted-foreground hover:text-[#002FA7] transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </section>
  );
}
